import { WorkflowNode } from './workflow-node.model';
import { WorkflowNodeType } from './workflow-node-type.enum';
import { WorkflowNodeRuleType } from './workflow-node-rule-type.enum';
import { WorkflowNodeActionType } from './workflow-node-action-type.enum';
import { extractReferencedMetadataKeys, needsMetadataDeclaration } from './placeholder-extractor';

/**
 * Pre-save linter for workflow graphs. Mirrors the constraints the runtime
 * (Osstre.Workflow.Processing) enforces, so authors get the feedback at
 * design time rather than as a failed run.
 *
 * Errors block save; warnings are surfaced but allow save.
 */

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: ValidationSeverity;
  code: string;
  message: string;
  /** Affected node, when the issue is node-local. */
  nodeId?: string;
  /** Branch that triggered the issue, for connectivity errors. */
  branch?: 'true' | 'false';
}

export interface ValidationResult {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export function validateWorkflow(nodes: WorkflowNode[]): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  if (nodes.length === 0) {
    return { errors, warnings };
  }

  const byId = new Map(nodes.map(n => [n.id, n]));
  const triggers = nodes.filter(n => n.type === WorkflowNodeType.Trigger);

  if (triggers.length === 0) {
    errors.push({
      severity: 'error',
      code: 'no_trigger',
      message: 'Workflow has no Trigger node — runtime will never enter it.'
    });
  }

  for (const node of nodes) {
    validateNode(node, byId, errors, warnings);
  }

  // Reachability: walk from every trigger, mark visited.
  const reachable = new Set<string>();
  const stack: string[] = triggers.map(t => t.id);
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (reachable.has(id)) continue;
    reachable.add(id);
    const node = byId.get(id);
    if (!node) continue;
    if (node.successorTrue && byId.has(node.successorTrue)) stack.push(node.successorTrue);
    if (node.successorFalse && byId.has(node.successorFalse)) stack.push(node.successorFalse);
  }

  for (const node of nodes) {
    if (!reachable.has(node.id)) {
      warnings.push({
        severity: 'warning',
        code: 'unreachable',
        nodeId: node.id,
        message: `Node "${node.name || 'Untitled'}" is unreachable from any Trigger.`
      });
    }
  }

  return { errors, warnings };
}

function validateNode(
  node: WorkflowNode,
  byId: Map<string, WorkflowNode>,
  errors: ValidationIssue[],
  warnings: ValidationIssue[]
): void {
  const label = node.name || 'Untitled';

  switch (node.type) {
    case WorkflowNodeType.Trigger:
      if (!node.definition.expression?.trim()) {
        errors.push(field(node, 'trigger_no_expression', `Trigger "${label}" has no event type.`));
      }
      requireSuccessor(node, byId, 'true', errors, label);
      break;

    case WorkflowNodeType.Action:
      if (!node.actionType) {
        errors.push(field(node, 'action_no_subtype', `Action "${label}" has no action type.`));
      } else if (node.actionType === WorkflowNodeActionType.SendEmail) {
        if (!node.definition.emailTemplateId?.trim()) {
          errors.push(field(node, 'email_no_template', `Send Email "${label}" has no template id.`));
        }
      } else if (node.actionType === WorkflowNodeActionType.Webhook) {
        if (!node.definition.url?.trim()) {
          errors.push(field(node, 'webhook_no_url', `Webhook "${label}" has no URL.`));
        }
      }
      requireSuccessor(node, byId, 'true', errors, label);
      break;

    case WorkflowNodeType.Rule:
      if (!node.ruleType) {
        errors.push(field(node, 'rule_no_subtype', `Rule "${label}" has no rule type.`));
        return;
      }
      if (node.ruleType === WorkflowNodeRuleType.Condition) {
        if (!node.definition.expression?.trim()) {
          errors.push(field(node, 'condition_no_expression', `Condition "${label}" has no expression.`));
        }
        requireSuccessor(node, byId, 'true', errors, label);
        requireSuccessor(node, byId, 'false', errors, label);
      } else if (node.ruleType === WorkflowNodeRuleType.WaitForTrigger) {
        if (!node.definition.expression?.trim()) {
          errors.push(field(node, 'waittrigger_no_expression', `WaitForTrigger "${label}" has no expression.`));
        } else if (extractReferencedMetadataKeys(node.definition).length === 0) {
          warnings.push(field(node, 'waittrigger_no_metadata', `WaitForTrigger "${label}" expression references no metadata key — may never fire.`, 'warning'));
        }
        requireSuccessor(node, byId, 'true', errors, label);
      } else if (node.ruleType === WorkflowNodeRuleType.TimeDelay) {
        if (!node.definition.delay) {
          errors.push(field(node, 'timedelay_no_delay', `TimeDelay "${label}" has no delay configured.`));
        }
        requireSuccessor(node, byId, 'true', errors, label);
      }
      break;

    case WorkflowNodeType.Terminator:
      if (node.successorTrue !== node.id) {
        warnings.push(field(node, 'terminator_self_loop', `Terminator "${label}" should reference itself in SuccessorTrue.`, 'warning'));
      }
      break;
  }

  // Required-metadata coverage: anything referenced in templated fields
  // should be declared so the runtime gate-checks before execution.
  const referenced = extractReferencedMetadataKeys(node.definition);
  const declared = new Set(node.requiredMetadata);
  const missing = referenced.filter(k => !declared.has(k) && needsMetadataDeclaration(k));
  if (missing.length > 0) {
    warnings.push(field(
      node,
      'metadata_missing',
      `Node "${label}" references ${missing.length === 1 ? 'key' : 'keys'} not declared in Required Metadata: ${missing.join(', ')}.`,
      'warning'
    ));
  }
}

function requireSuccessor(
  node: WorkflowNode,
  byId: Map<string, WorkflowNode>,
  branch: 'true' | 'false',
  errors: ValidationIssue[],
  label: string
): void {
  const targetId = branch === 'true' ? node.successorTrue : node.successorFalse;
  if (!targetId) {
    errors.push({
      severity: 'error',
      code: 'missing_successor',
      nodeId: node.id,
      branch,
      message: branch === 'false'
        ? `Condition "${label}" has no False branch successor.`
        : `Node "${label}" has no successor.`
    });
    return;
  }
  if (!byId.has(targetId)) {
    errors.push({
      severity: 'error',
      code: 'dangling_successor',
      nodeId: node.id,
      branch,
      message: `Node "${label}" successor (${branch}) references a node that no longer exists.`
    });
  }
}

function field(
  node: WorkflowNode,
  code: string,
  message: string,
  severity: ValidationSeverity = 'error'
): ValidationIssue {
  return { severity, code, message, nodeId: node.id };
}
